// =============================
// Email: info@ebenmonney.com
// www.ebenmonney.com/templates
// =============================

import { Injectable, Output, EventEmitter } from '@angular/core';
import { Router, NavigationExtras } from "@angular/router";
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, forkJoin } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';

import { AccountEndpoint } from './account-endpoint.service';
import { AuthService } from './auth.service';
import { User } from '../models/user.model';
import { Role } from '../models/role.model';
import { Permission, PermissionNames, PermissionValues } from '../models/permission.model';
import { UserEdit } from '../models/user-edit.model';
import { ContulMeu } from '../models/contulMeu.model';
import { Poze } from '../models/poze.model';
import { Companie } from '../models/companie.model';

import { of } from 'rxjs';
import { DetaliuCircuit } from '../models/DetaliuCircuit.model';
import { Compensare } from '../models/Compensare.model';
import { environment } from 'src/environments/environment';
import { Utilities } from './utilities';
import { Car } from '../models/Car.model';
import { Product, GetPhotosWitPoductGroupsViewModel } from '../models/Product.model';
import { Offer, OfferVM } from '../models/offer.model';
import { OfferVm } from '../models/offerVm.model';
import { ExcelFisier } from '../models/ExcelFisier.model';
import { CatalogLink } from '../models/catalogLink.model';
import { AuditLogin } from '../models/auditLogin.model';

export type RolesChangedOperation = "add" | "delete" | "modify";
export type RolesChangedEventArg = { roles: Role[] | string[], operation: RolesChangedOperation };

@Injectable()
export class AccountService {
  public static readonly roleAddedOperation: RolesChangedOperation = "add";
  public static readonly roleDeletedOperation: RolesChangedOperation = "delete";
  public static readonly roleModifiedOperation: RolesChangedOperation = "modify";

  private _rolesChanged = new Subject<RolesChangedEventArg>();

  constructor(
    private router: Router,
    private http: HttpClient,
    private authService: AuthService,
    private accountEndpoint: AccountEndpoint) { }

  isOpen = false;
  @Output() change: EventEmitter<boolean> = new EventEmitter();

  toggle() {
    this.isOpen = !this.isOpen;
    this.change.emit(this.isOpen);
  }

  getUser(userId?: string) {
    return this.accountEndpoint.getUserEndpoint<User>(userId);
  }

  getUserAndRoles(userId?: string) {

    return forkJoin(
      this.accountEndpoint.getUserEndpoint<User>(userId),
      this.accountEndpoint.getRolesEndpoint<Role[]>());
  }

  getUsers(page?: number, pageSize?: number) {

    return this.accountEndpoint.getUsersEndpoint<User[]>(page, pageSize);
  }

  getUsersAndRoles(page?: number, pageSize?: number) {

    return forkJoin(
      this.accountEndpoint.getUsersEndpoint<User[]>(page, pageSize),
      this.accountEndpoint.getRolesEndpoint<Role[]>());
  }

  updateUser(user: UserEdit) {
    if (user.id) {
      return this.accountEndpoint.getUpdateUserEndpoint(user, user.id);
    }
    else {
      return this.accountEndpoint.getUserByUserNameEndpoint<User>(user.userName).pipe<User>(
        mergeMap(foundUser => {
          user.id = foundUser.id;
          return this.accountEndpoint.getUpdateUserEndpoint(user, user.id)
        }));
    }
  }

  newUser(user: UserEdit) {
    return this.accountEndpoint.getNewUserEndpoint<User>(user);
  }

  newUserFromOutside(user: UserEdit) {
    return this.accountEndpoint.getNewUserFromOutsideEndpoint<User>(user);
  }

  sendMailForPasswordRecovery(user: UserEdit) {
    return this.accountEndpoint.sendMailForPasswordRecoveryEndpoint<User>(user);
  }

  getUserPreferences() {
    return this.accountEndpoint.getUserPreferencesEndpoint<string>();
  }

  updateUserPreferences(configuration: string) {
    return this.accountEndpoint.getUpdateUserPreferencesEndpoint(configuration);
  }

  deleteUser(userOrUserId: string | User): Observable<User> {
    if (typeof userOrUserId === 'string' || userOrUserId instanceof String) {
      return this.accountEndpoint.getDeleteUserEndpoint<User>(<string>userOrUserId).pipe<User>(
        tap(data => this.onRolesUserCountChanged(data.roles)));
    }
    else {
      if (userOrUserId.id) {
        return this.deleteUser(userOrUserId.id);
      }
      else {
        return this.accountEndpoint.getUserByUserNameEndpoint<User>(userOrUserId.userName).pipe<User>(
          mergeMap(user => this.deleteUser(user.id)));
      }
    }
  }

  unblockUser(userId: string) {
    return this.accountEndpoint.getUnblockUserEndpoint(userId);
  }

  userHasPermission(permissionValue: PermissionValues): boolean {
    return this.permissions.some(p => p == permissionValue);
  }

  refreshLoggedInUser() {
    return this.authService.refreshLogin();
  }

  getRoles(page?: number, pageSize?: number) {

    return this.accountEndpoint.getRolesEndpoint<Role[]>(page, pageSize);
  }

  getRolesAndPermissions(page?: number, pageSize?: number) {

    return forkJoin(
      this.accountEndpoint.getRolesEndpoint<Role[]>(page, pageSize),
      this.accountEndpoint.getPermissionsEndpoint<Permission[]>());
  }

  updateRole(role: Role) {
    if (role.id) {
      return this.accountEndpoint.getUpdateRoleEndpoint(role, role.id).pipe(
        tap(data => this.onRolesChanged([role], AccountService.roleModifiedOperation)));
    }
    else {
      return this.accountEndpoint.getRoleByRoleNameEndpoint<Role>(role.name).pipe(
        mergeMap(foundRole => {
          role.id = foundRole.id;
          return this.accountEndpoint.getUpdateRoleEndpoint(role, role.id)
        }),
        tap(data => this.onRolesChanged([role], AccountService.roleModifiedOperation)));
    }
  }

  newRole(role: Role) {
    return this.accountEndpoint.getNewRoleEndpoint<Role>(role).pipe<Role>(
      tap(data => this.onRolesChanged([role], AccountService.roleAddedOperation)));
  }

  deleteRole(roleOrRoleId: string | Role): Observable<Role> {

    if (typeof roleOrRoleId === 'string' || roleOrRoleId instanceof String) {
      return this.accountEndpoint.getDeleteRoleEndpoint<Role>(<string>roleOrRoleId).pipe<Role>(
        tap(data => this.onRolesChanged([data], AccountService.roleDeletedOperation)));
    }
    else {

      if (roleOrRoleId.id) {
        return this.deleteRole(roleOrRoleId.id);
      }
      else {
        return this.accountEndpoint.getRoleByRoleNameEndpoint<Role>(roleOrRoleId.name).pipe<Role>(
          mergeMap(role => this.deleteRole(role.id)));
      }
    }
  }

  getPermissions() {

    return this.accountEndpoint.getPermissionsEndpoint<Permission[]>();
  }

  private onRolesChanged(roles: Role[] | string[], op: RolesChangedOperation) {
    this._rolesChanged.next({ roles: roles, operation: op });
  }

  onRolesUserCountChanged(roles: Role[] | string[]) {
    return this.onRolesChanged(roles, AccountService.roleModifiedOperation);
  }

  getRolesChangedEvent(): Observable<RolesChangedEventArg> {
    return this._rolesChanged.asObservable();
  }

  get permissions(): PermissionValues[] {
    return this.authService.userPermissions;
  }

  get currentUser() {
    return this.authService.currentUser;
  }

  newContulMeu(contulMeu: ContulMeu) {
    return this.accountEndpoint.getNewContulMeuEndpoint<ContulMeu>(contulMeu);
  }

  newPoze(poze: Poze) {
    return this.accountEndpoint.getNewPozeEndpoint<Poze>(poze);
  }

  getUserContulMeu() {
    return this.accountEndpoint.getUserContulMeuEndpoint<ContulMeu>();
  }

  getUserPoze() {
    return this.accountEndpoint.getUserPozeEndpoint<Poze>();
  }

  getContulMeuList(nume?: string, prenume?: string, document?: string) {
    return forkJoin(
      this.accountEndpoint.getContulMeuListEndpoint<ContulMeu[]>(nume, prenume, document)
    );
  }

  aprobareContulMeu(contulMeu: ContulMeu) {
    return this.accountEndpoint.getAprobareContulMeuEndpoint<ContulMeu>(contulMeu);
  }

  newCompanie(companie: Companie) {
    return this.accountEndpoint.getNewCompanieEndpoint<Companie>(companie);
  }

  getCompaniiReprezentateListForAutocomplete(denumire?: string) {
    return forkJoin(
      this.accountEndpoint.getCompaniiReprezentateAutocompleteListEndpoint<Companie[]>(denumire, denumire, "Y")
    );
  }

  getCompaniiReprezentateList(denumire?: string, cif?: string, all?: string) {
    return forkJoin(
      this.accountEndpoint.getCompaniiReprezentateListEndpoint<Companie[]>(denumire, cif, all)
    );
  }

  aprobareReprezentareCompanie(companie: Companie) {
    return this.accountEndpoint.aprobareReprezentareCompanieEndpoint<Companie>(companie);
  }

  selectieCompanie(companie: Companie) {
    return this.accountEndpoint.selectieCompanieEndpoint<Companie>(companie);
  }

  getCompanieSelectata() {
    return this.accountEndpoint.getCompanieSelectataEndpoint<Companie>();
  }
  
  confirmUser(userId: string) {
    return this.accountEndpoint.confirmUserEndpoint<User>(userId);
  }

  getCompaniiListForAutocomplete(denumire?: string) {
    return forkJoin(
      this.accountEndpoint.getCompaniiListForAutocompleteEndpoint<Companie[]>(denumire, "undefined", "Y")
    );
  }
  
  downloadDoc(document: string): Observable<any> {
    return forkJoin(
      this.accountEndpoint.downloadDocEndpoint<Compensare>(document)
    );
  }
  
  downloadOffer(tip_export: string, document: string, browserLang: string): Observable<any> {
    return forkJoin(
      this.accountEndpoint.downloadOfferEndpoint<Offer>(tip_export,document, browserLang)
    );
  }

  uploadFile(tipIncarcare: string, file: any) {
    return forkJoin(
      this.accountEndpoint.uploadEndpoint<ExcelFisier>(tipIncarcare, file)
    );
  }

  uploadPriceFile(tipIncarcare: string, file: any) {
    return forkJoin(
      this.accountEndpoint.uploadPriceEndpoint<ExcelFisier>(tipIncarcare, file)
    );
  }
  
  uploadFisierDocumentCI(idDetaliu: string, file: any) {
    return forkJoin(
      this.accountEndpoint.uploadFisierDocumentCIEndpoint<any>(idDetaliu, file)
    );
  }

  downloadDocCI(document: string): Observable<any> {
    return forkJoin(
      this.accountEndpoint.downloadDocCIEndpoint<Compensare>(document)
    );
  }
  
  uploadFisierDocumentCompanie(idReprezentant: string, idCompanie: string, file: any, mod:string) {
    return forkJoin(
      this.accountEndpoint.uploadFisierDocumentCompanieEndpoint<any>(idReprezentant, idCompanie, file, mod)
    );
  }

  downloadDocumentCompanie(idReprezentant: string, idCompanie: string, mod: string): Observable<any> {
    return forkJoin(
      this.accountEndpoint.downloadDocumentCompanieEndpoint<Compensare>(idReprezentant, idCompanie, mod)
    );
  }
  
  getCarsList(make?: string, model?: string) {
    return forkJoin(
      this.accountEndpoint.getCarsListEndpoint<Companie[]>(make, model)
    );
  }

  getCarsConfiguratorLists(make: string = 'undefined', model: string = 'undefined', body: string = 'undefined', variant: string = 'undefined', wheelbase: string = 'undefined', height: string = 'undefined', transmission: string = 'undefined', reference: string = 'undefined') {
    return forkJoin(
      this.accountEndpoint.getCarsConfiguratorListEndpoint<Companie[]>(make, model, body, variant, wheelbase, height, transmission, reference)
    );
  }

  saveCar(car: Car) {
    return this.accountEndpoint.saveCarEndpoint<Car>(car);
  }

  getProductLists(reference?: string, description?: string, carReference?: string, pricedealer?: string, pricefinalcustomer?: string, descriptionfr?: string, productgrup?: string, VehicleReference?: string, descriptionro?: string, productnumber?: string, catalogreference?: string, linkCatalog?: string, descriptionen?: string, carSelect?: string) {
    return forkJoin(
      this.accountEndpoint.getProductListEndpoint<Product[]>(reference, description, carReference, pricedealer, pricefinalcustomer, descriptionfr, productgrup, VehicleReference, descriptionro, productnumber, catalogreference, linkCatalog, descriptionen, carSelect)
    );
  }

  getProductConfiguratorLists(reference: string = 'undefined', description: string = 'undefined', pricedealer: string = 'undefined', pricefinalcustomer: string = 'undefined', productgrup: string = 'undefined') {
    return forkJoin(
      this.accountEndpoint.getProductConfiguratorListEndpoint<Companie[]>(reference, description, pricedealer, pricefinalcustomer, productgrup)
    );
  }

  saveProduct(product: Product) {
    return this.accountEndpoint.saveProductEndpoint<Product>(product);
  }

  getCatalogLinkLists(catalogreference?: string) {
    return forkJoin(
      this.accountEndpoint.getCatalogLinkListEndpoint<CatalogLink[]>(catalogreference)
    );
  }

  getCatalogLinkConfiguratorLists() {
    return forkJoin(
      this.accountEndpoint.getCatalogLinkConfiguratorListEndpoint<CatalogLink[]>()
    );
  }

  saveCatalogLink(catalogLink: CatalogLink) {
    return this.accountEndpoint.saveCatalogLinkEndpoint<CatalogLink>(catalogLink);
  }

  getPozeLists(productgroup?: string, vehicleReference?: string, photoReference?: string, carReference?: string, productGrup?: string) {
    return forkJoin(
      this.accountEndpoint.getPozeListEndpoint<Poze[]>(productgroup, vehicleReference, photoReference, carReference, productGrup)
    );
  }

  getPhotosWitPoductGroupsViewModel(getPhotosWitPoductGroupsViewModel: GetPhotosWitPoductGroupsViewModel) {
    return this.accountEndpoint.getPhotosWitPoductGroupsViewModelEndpoint<Poze[]>(getPhotosWitPoductGroupsViewModel);
  }

  getPozeConfiguratorLists(productGroup: string = 'undefined', vehicleReference: string = 'undefined', photoReference: string = 'undefined') {
    return forkJoin(
      this.accountEndpoint.getPozeConfiguratorListEndpoint<Poze[]>(productGroup, vehicleReference, photoReference)
    );
  }

  uploadPhoto(productGroup: string, vehicleReference: string, photoReference: string, file: any) {
    return this.accountEndpoint.uploadPhotoEndpoint<Poze>(productGroup, vehicleReference, photoReference, file);
  }

  savePoze(poze: Poze) {
    return this.accountEndpoint.savePozeEndpoint<Poze>(poze);
  }

  getOfferList(id: string, subject: string, initialDate: string, finalDate: string) {
    return forkJoin(
      this.accountEndpoint.getOfferListEndpoint<OfferVm>(id, subject, initialDate, finalDate)
    );
  }

  getOfferConfiguratorLists(usergenerare: string = 'undefined', offerdetails: string = 'undefined') {
    return forkJoin(
      this.accountEndpoint.getOfferConfiguratorListEndpoint<OfferVm[]>(usergenerare, offerdetails)
    );
  }

  saveOffer(offer: OfferVM) {
    return this.accountEndpoint.saveOfferEndpoint<OfferVM>(offer);
  }

  saveOfferManualDetails(offerId: string, manualRows: any) {
    return this.accountEndpoint.saveOfferManualDetailsEndpoint<any>(offerId, manualRows);
  }

  getOfferListProductPhoto(id: string) {
    return forkJoin(
      this.accountEndpoint.getOfferListProductPhotoEndpoint<OfferVM>(id)
    );
  }

  deleteCompany(companie: Companie) {
    return this.accountEndpoint.getDeleteCompanyEndpoint<Companie>(companie);
  }

  getAuditLoginList(userName: string, initialDate: string, finalDate: string) {
    return forkJoin(
      this.accountEndpoint.getAuditLoginListEndpoint<AuditLogin[]>(userName, initialDate, finalDate)
    );
  }

  cloneOffer(offer: OfferVM) {
    return this.accountEndpoint.cloneOfferEndpoint<OfferVM>(offer);
  }

}
